import React from "react";
import { useRecoilValue } from "recoil";
import { PatientState } from "../../data/atoms";
import { RLGLScore } from "../../models/reports";
import { convertDOBintoAge, getPatientAge } from "../../utils/sharedFunction";
import CustomBarGraph from "../Graphs/CustomBarGraph";

interface Props {
  score: RLGLScore;
}

const RLGL_Test: React.FC<Props> = ({ score }) => {
  const patientInfo = useRecoilValue(PatientState);

  const averageSpeedOption = {
    graphName: "AverageSpeed",
    graphSVG: (
      <svg
        width="41"
        height="42"
        viewBox="0 0 41 42"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <g filter="url(#filter0_d_8478_39826)">
          <path
            d="M33.8966 17.5C33.8966 24.9566 27.8763 31 20.4483 31C13.0202 31 7 24.9566 7 17.5C7 10.0434 13.0202 4 20.4483 4C27.8763 4 33.8966 10.0434 33.8966 17.5ZM22.1293 9.0625C22.1293 8.1291 21.3781 7.375 20.4483 7.375C19.5185 7.375 18.7672 8.1291 18.7672 9.0625C18.7672 9.9959 19.5185 10.75 20.4483 10.75C21.3781 10.75 22.1293 9.9959 22.1293 9.0625ZM20.4483 25.9375C22.3027 25.9375 23.8103 24.424 23.8103 22.5625C23.8103 21.6449 23.4479 20.817 22.8595 20.2105L26.2268 12.5271C26.5053 11.8891 26.2163 11.1402 25.5807 10.8607C24.945 10.5813 24.1991 10.8713 23.9207 11.5094L20.5481 19.1875C20.5166 19.1875 20.4798 19.1875 20.4483 19.1875C18.5939 19.1875 17.0862 20.701 17.0862 22.5625C17.0862 24.424 18.5939 25.9375 20.4483 25.9375ZM16.2457 11.5938C16.2457 10.6604 15.4945 9.90625 14.5647 9.90625C13.6348 9.90625 12.8836 10.6604 12.8836 11.5938C12.8836 12.5271 13.6348 13.2812 14.5647 13.2812C15.4945 13.2812 16.2457 12.5271 16.2457 11.5938ZM12.0431 19.1875C12.9729 19.1875 13.7241 18.4334 13.7241 17.5C13.7241 16.5666 12.9729 15.8125 12.0431 15.8125C11.1133 15.8125 10.3621 16.5666 10.3621 17.5C10.3621 18.4334 11.1133 19.1875 12.0431 19.1875ZM30.5345 17.5C30.5345 16.5666 29.7833 15.8125 28.8534 15.8125C27.9236 15.8125 27.1724 16.5666 27.1724 17.5C27.1724 18.4334 27.9236 19.1875 28.8534 19.1875C29.7833 19.1875 30.5345 18.4334 30.5345 17.5Z"
            fill="black"
          />
        </g>
        <defs>
          <filter
            id="filter0_d_8478_39826"
            x="0.0538921"
            y="0.526946"
            width="40.7887"
            height="40.8922"
            filterUnits="userSpaceOnUse"
            colorInterpolationFilters="sRGB"
          >
            <feFlood floodOpacity="0" result="BackgroundImageFix" />
            <feColorMatrix
              in="SourceAlpha"
              type="matrix"
              values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0"
              result="hardAlpha"
            />
            <feOffset dy="3.47305" />
            <feGaussianBlur stdDeviation="3.47305" />
            <feComposite in2="hardAlpha" operator="out" />
            <feColorMatrix
              type="matrix"
              values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0.25 0"
            />
            <feBlend
              mode="normal"
              in2="BackgroundImageFix"
              result="effect1_dropShadow_8478_39826"
            />
            <feBlend
              mode="normal"
              in="SourceGraphic"
              in2="effect1_dropShadow_8478_39826"
              result="shape"
            />
          </filter>
        </defs>
      </svg>
    ),
    graphTitle: "Average Speed",
    graphTitleDesc: "(m/s)",
    graphNormValue: 0,
    isgraphNorm: false,
    graphActualValue: [score.averageSpeed],
    graphActualValueLabel: [`${score.averageSpeed}`],
    graphRange: [0.25, 0.5, 0.75, 1],
    graphRangeLabel: [".25", ".50", ".75", "1"],
    graphUnit: "",
    graphNoLines: [],
    minMax: false,
    graphAge: convertDOBintoAge(new Date(patientInfo?.dob)),
  };

  const distanceOption = {
    graphName: "Distance",
    graphSVG: (
      <svg
        xmlns="http://www.w3.org/2000/svg"
        width="49"
        height="36"
        viewBox="0 0 49 36"
        fill="none"
      >
        <g filter="url(#filter0_d_4669_308)">
          <path
            d="M8.30103 14.8873C9.80847 17.4983 13.8029 20.9639 13.8029 20.9639L20.6651 19.0374C19.5292 17.0699 19.7243 15.6815 19.9195 14.293C20.1146 12.9045 20.3098 11.516 19.1739 9.54858C17.8392 7.23684 14.1512 2.57544 9.86232 3.77949C5.57347 4.98354 6.02923 10.9525 8.30103 14.8873ZM20.2631 25.2471C22.1555 24.7158 23.015 23.1075 22.1796 21.6606L21.4223 20.349L14.5602 22.2755L15.3174 23.5871C16.1528 25.034 18.3706 25.7784 20.2631 25.2471ZM38.7787 12.428C36.5069 8.49307 30.9046 3.969 26.6157 5.17305C22.3269 6.37709 24.2994 11.5201 25.6341 13.8318C26.77 15.7993 28.2903 16.7061 29.8107 17.613C31.3311 18.5199 32.8515 19.4267 33.9874 21.3942L40.8495 19.4677C40.8495 19.4677 40.2862 15.0389 38.7787 12.428ZM40.4475 25.6774C42.34 25.1462 43.1994 23.5378 42.3641 22.0909L41.6068 20.7793L34.7446 22.7058L35.5019 24.0174C36.3373 25.4643 38.5551 26.2087 40.4475 25.6774Z"
            fill="black"
          />
        </g>
        <defs>
          <filter
            id="filter0_d_4669_308"
            x="0.423875"
            y="0.494286"
            width="48.4394"
            height="34.6242"
            filterUnits="userSpaceOnUse"
            colorInterpolationFilters="sRGB"
          >
            <feFlood floodOpacity="0" result="BackgroundImageFix" />
            <feColorMatrix
              in="SourceAlpha"
              type="matrix"
              values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0"
              result="hardAlpha"
            />
            <feOffset dy="3.08982" />
            <feGaussianBlur stdDeviation="3.08982" />
            <feComposite in2="hardAlpha" operator="out" />
            <feColorMatrix
              type="matrix"
              values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0.25 0"
            />
            <feBlend
              mode="normal"
              in2="BackgroundImageFix"
              result="effect1_dropShadow_4669_308"
            />
            <feBlend
              mode="normal"
              in="SourceGraphic"
              in2="effect1_dropShadow_4669_308"
              result="shape"
            />
          </filter>
        </defs>
      </svg>
    ),
    graphTitle: "Distance",
    graphTitleDesc: "(m)",
    graphNormValue: 0,
    isgraphNorm: false,
    graphActualValue: [score.distance],
    graphActualValueLabel: [`${score.distance}`],
    graphRange: [15, 30, 45, 60],
    graphRangeLabel: ["15", "30", "45", "60"],
    graphNoLines: [],
    minMax: false,
    graphUnit: "",
    graphAge: convertDOBintoAge(new Date(patientInfo?.dob)),
  };

  const redLightsMissedOption = {
    graphName: "RedLightsMissed",
    graphSVG: (
      <svg
        width="28"
        height="37"
        viewBox="0 0 28 37"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <g filter="url(#filter0_d_8478_39751)">
          <path
            d="M22.8873 2.79979C22.9098 2.78525 23.1507 2.63091 23.3886 2.36324C23.4257 2.32149 23.9528 1.72016 23.7897 1.53338C23.6614 1.3865 23.2139 1.61838 22.4365 1.79546C20.8424 2.1582 19.7485 1.82976 19.4791 2.31962C19.4462 2.37927 19.4185 2.4639 19.429 2.5817V8.30234C19.6127 8.22964 19.7968 8.15694 19.9805 8.08387C21.713 4.26378 22.5065 3.04733 22.8877 2.79979H22.8873Z"
            fill="black"
          />
          <path
            d="M22.3344 2.45118C22.1332 2.56488 21.9409 2.69723 21.7228 2.7867C21.505 2.87617 21.2806 2.87505 21.0452 2.8881C20.5884 2.91345 20.211 3.00591 19.8653 3.27507C19.7789 3.34255 19.6909 3.41487 19.6079 3.49241V2.56898C19.6012 2.49815 19.612 2.43813 19.6404 2.38631C19.7407 2.20438 20.0591 2.18239 20.6599 2.15666C21.1511 2.13579 21.7624 2.10932 22.4815 1.94603C22.7598 1.88266 22.9977 1.8122 23.1889 1.75553C23.3827 1.69812 23.5514 1.64816 23.642 1.64294C23.6337 1.72011 23.5409 1.89309 23.3962 2.08471C23.025 2.13691 22.6614 2.26627 22.3348 2.45118H22.3344Z"
            fill="black"
          />
          <path
            d="M19.9662 7.66674C20.6232 6.40071 21.2801 5.13468 21.9367 3.86864C20.9842 3.46155 19.9142 3.34747 19.3093 3.76463C18.4429 4.36223 18.3718 6.15914 19.9662 7.66674Z"
            fill="black"
          />
          <path
            d="M22.8873 20.9576C22.9098 20.9431 23.1507 20.7887 23.3886 20.5211C23.4257 20.4793 23.9528 19.878 23.7897 19.6912C23.6614 19.5443 23.2139 19.7762 22.4365 19.9533C20.8424 20.316 19.7485 19.9876 19.4791 20.4775C19.4462 20.5371 19.4185 20.6217 19.429 20.7395V26.4605C19.6127 26.3878 19.7968 26.3152 19.9805 26.2421C21.713 22.422 22.5065 21.2055 22.8877 20.958L22.8873 20.9576Z"
            fill="black"
          />
          <path
            d="M22.3344 20.609C22.1332 20.7227 21.9409 20.8551 21.7228 20.9445C21.505 21.034 21.2806 21.0329 21.0452 21.0459C20.5884 21.0713 20.211 21.1637 19.8653 21.4329C19.7789 21.5004 19.6909 21.5727 19.6079 21.6502V20.7268C19.6012 20.656 19.612 20.596 19.6404 20.5441C19.7407 20.3622 20.0591 20.3402 20.6599 20.3145C21.1511 20.2936 21.7624 20.2672 22.4815 20.1039C22.7598 20.0405 22.9977 19.97 23.1889 19.9134C23.3827 19.856 23.5514 19.806 23.642 19.8008C23.6337 19.878 23.5409 20.0509 23.3962 20.2426C23.025 20.2947 22.6614 20.4241 22.3348 20.609H22.3344Z"
            fill="black"
          />
          <path
            d="M19.9662 25.8244C20.6232 24.5584 21.2801 23.2923 21.9367 22.0263C20.9842 21.6192 19.9142 21.5051 19.3093 21.9223C18.4429 22.5199 18.3718 24.3168 19.9662 25.8244Z"
            fill="black"
          />
          <path
            d="M23.0665 11.3149C23.089 11.3004 23.3299 11.146 23.5678 10.8784C23.6049 10.8366 24.132 10.2353 23.9689 10.0485C23.8406 9.90164 23.3931 10.1335 22.6157 10.3106C21.0216 10.6733 19.9277 10.3449 19.6583 10.8348C19.6254 10.8944 19.5977 10.979 19.6082 11.0968V16.8178C19.7919 16.7451 19.976 16.6725 20.1597 16.5994C21.8922 12.7793 22.6857 11.5628 23.0669 11.3153L23.0665 11.3149Z"
            fill="black"
          />
          <path
            d="M22.5136 10.9663C22.3124 11.08 22.1201 11.2123 21.902 11.3018C21.6842 11.3912 21.4598 11.3901 21.2244 11.4032C20.7676 11.4285 20.3902 11.521 20.0445 11.7901C19.9581 11.8576 19.8701 11.9299 19.7871 12.0075V11.0841C19.7804 11.0132 19.7912 10.9532 19.8196 10.9014C19.9199 10.7195 20.2383 10.6975 20.8391 10.6717C21.3303 10.6509 21.9416 10.6244 22.6607 10.4611C22.939 10.3977 23.1769 10.3273 23.3681 10.2706C23.5619 10.2132 23.7306 10.1632 23.8212 10.158C23.8129 10.2352 23.7201 10.4082 23.5754 10.5998C23.2042 10.652 22.8406 10.7813 22.514 10.9663H22.5136Z"
            fill="black"
          />
          <path
            d="M20.1454 16.1819C20.8024 14.9158 21.4593 13.6498 22.1159 12.3838C21.1634 11.9767 20.0934 11.8626 19.4885 12.2798C18.6221 12.8774 18.551 14.6743 20.1454 16.1819Z"
            fill="black"
          />
          <path
            d="M4.96098 2.69243C4.94152 2.67789 4.73314 2.52355 4.52775 2.25588C4.49558 2.21412 4.04028 1.6128 4.18133 1.42602C4.29244 1.27914 4.6789 1.51102 5.3508 1.6881C6.72829 2.05084 7.67367 1.7224 7.90637 2.21226C7.9348 2.27191 7.95875 2.35653 7.94977 2.47434V8.19498C7.79077 8.12228 7.63215 8.04958 7.47315 7.97651C5.97632 4.15642 5.29057 2.93997 4.96098 2.69243Z"
            fill="black"
          />
          <path
            d="M5.43853 2.34376C5.61249 2.45746 5.77897 2.5898 5.96715 2.67928C6.15533 2.76875 6.34912 2.76763 6.55264 2.78068C6.94733 2.80603 7.27393 2.89848 7.57247 3.16765C7.6473 3.23512 7.72287 3.30745 7.79507 3.38499V2.46156C7.80106 2.39073 7.7917 2.33071 7.76701 2.27889C7.68059 2.09696 7.40524 2.07497 6.88597 2.04924C6.46136 2.02837 5.93311 2.0019 5.31171 1.83861C5.07115 1.77523 4.86539 1.70477 4.7004 1.64811C4.5328 1.5907 4.38727 1.54074 4.30908 1.53552C4.31619 1.61269 4.39625 1.78567 4.52158 1.97729C4.84219 2.02948 5.15645 2.15885 5.4389 2.34376H5.43853Z"
            fill="black"
          />
          <path
            d="M7.48568 7.5592C6.91815 6.29317 6.35024 5.02713 5.78271 3.7611C6.60576 3.354 7.53057 3.23992 8.05321 3.65709C8.80181 4.25469 8.86354 6.05159 7.48568 7.5592Z"
            fill="black"
          />
          <path
            d="M4.96098 20.8502C4.94152 20.8357 4.73314 20.6813 4.52775 20.4137C4.49558 20.3719 4.04028 19.7706 4.18133 19.5838C4.29244 19.4369 4.6789 19.6688 5.3508 19.8459C6.72829 20.2086 7.67367 19.8802 7.90637 20.37C7.9348 20.4297 7.95875 20.5143 7.94977 20.6321V26.3531C7.79077 26.2804 7.63215 26.2077 7.47315 26.1347C5.97595 22.3146 5.2902 21.0981 4.9606 20.8506L4.96098 20.8502Z"
            fill="black"
          />
          <path
            d="M5.43853 20.5017C5.61249 20.6154 5.77897 20.7478 5.96715 20.8372C6.15533 20.9267 6.34912 20.9256 6.55264 20.9386C6.94733 20.964 7.27393 21.0564 7.57247 21.3256C7.6473 21.3931 7.72287 21.4654 7.79507 21.543V20.6195C7.80106 20.5487 7.7917 20.4887 7.76701 20.4368C7.68059 20.2549 7.40524 20.2329 6.88597 20.2072C6.46136 20.1863 5.93311 20.1599 5.31171 19.9966C5.07115 19.9332 4.86539 19.8627 4.7004 19.8061C4.5328 19.7487 4.38727 19.6987 4.30908 19.6935C4.31619 19.7707 4.39625 19.9436 4.52158 20.1353C4.84219 20.1874 5.15645 20.3168 5.4389 20.5017H5.43853Z"
            fill="black"
          />
          <path
            d="M7.48568 25.7172C6.91815 24.4511 6.35024 23.1851 5.78271 21.9191C6.60576 21.512 7.53057 21.3979 8.05321 21.815C8.80181 22.4126 8.86354 24.2096 7.48568 25.7172Z"
            fill="black"
          />
          <path
            d="M4.80668 11.2077C4.78723 11.1931 4.57885 11.0388 4.37346 10.7711C4.34128 10.7294 3.88599 10.1281 4.02703 9.94128C4.13814 9.7944 4.5246 10.0263 5.19651 10.2034C6.57399 10.5661 7.51938 10.2377 7.75208 10.7275C7.78051 10.7872 7.80445 10.8718 7.79547 10.9896V16.7106C7.63647 16.6379 7.47785 16.5652 7.31885 16.4921C5.82165 12.6721 5.1359 11.4556 4.80631 11.2081L4.80668 11.2077Z"
            fill="black"
          />
          <path
            d="M5.28423 10.8591C5.4582 10.9728 5.62468 11.1052 5.81286 11.1947C6.00103 11.2841 6.19483 11.283 6.39834 11.2961C6.79303 11.3214 7.11963 11.4139 7.41818 11.683C7.493 11.7505 7.56857 11.8228 7.64077 11.9004V10.9769C7.64676 10.9061 7.63741 10.8461 7.61272 10.7943C7.5263 10.6123 7.25095 10.5903 6.73168 10.5646C6.30706 10.5437 5.77881 10.5173 5.15741 10.354C4.91685 10.2906 4.71109 10.2202 4.54611 10.1635C4.3785 10.1061 4.23297 10.0561 4.15479 10.0509C4.16189 10.1281 4.24195 10.3011 4.36728 10.4927C4.6879 10.5449 5.00215 10.6742 5.28461 10.8591H5.28423Z"
            fill="black"
          />
          <path
            d="M7.33089 16.0747C6.76336 14.8087 6.19546 13.5426 5.62793 12.2766C6.45098 11.8695 7.37579 11.7554 7.89842 12.1726C8.64703 12.7702 8.70875 14.5671 7.33089 16.0747Z"
            fill="black"
          />
          <path
            d="M18.9706 0.264404H8.74719C7.90791 0.264404 7.22754 0.942388 7.22754 1.77872V27.2213C7.22754 28.0576 7.90791 28.7356 8.74719 28.7356H18.9706C19.8099 28.7356 20.4902 28.0576 20.4902 27.2213V1.77872C20.4902 0.942388 19.8099 0.264404 18.9706 0.264404Z"
            fill="black"
          />
          <path
            d="M10.3242 11.86C9.57152 11.5976 8.71069 10.9086 8.46639 9.88305C8.15588 8.57936 8.91794 7.0546 10.4275 6.24115C10.4701 6.46148 10.8027 8.03768 12.337 8.89438C13.8185 9.72162 15.2652 9.23288 15.4848 9.15459C14.1215 11.3921 11.9404 12.4241 10.3242 11.86Z"
            fill="black"
          />
          <path
            d="M10.3438 20.5038C9.59105 20.2414 8.73022 19.5524 8.48592 18.5268C8.17541 17.2232 8.93748 15.6984 10.447 14.8849C10.4897 15.1053 10.8223 16.6815 12.3565 17.5382C13.838 18.3654 15.2847 17.8767 15.5043 17.7984C14.141 20.0359 11.9599 21.0679 10.3438 20.5038Z"
            fill="black"
          />
          <path
            d="M17.7546 25.7181C17.7516 25.7122 17.7531 25.7051 17.7557 25.6995C17.8036 20.7069 17.5631 16.5248 17.2914 13.3691C16.9768 9.71898 16.6715 8.03727 15.3303 6.86556C12.9768 4.80993 9.15149 5.82619 8.10547 6.1371C8.11108 5.01944 8.11669 3.90141 8.12193 2.78376C8.12193 1.73656 8.96368 0.888062 10.0022 0.888062H17.7546C18.7931 0.888062 19.6349 1.73693 19.6349 2.78376V23.8224C19.6211 24.0998 19.5466 24.7168 19.0842 25.163C18.5937 25.6365 17.7931 25.7957 17.7546 25.7181Z"
            fill="black"
          />
          <path
            d="M17.2782 5.47588C17.1428 5.5922 15.8431 3.22528 13.8809 3.21634C11.9041 3.20739 10.5767 5.59704 10.4364 5.47588C10.2823 5.34279 11.3953 2.02673 13.8573 2.0271C16.3205 2.0271 17.4286 5.34727 17.2782 5.47588Z"
            fill="black"
          />
          <path
            d="M10.4181 6.25658C10.6116 6.06906 11.8588 7.97146 13.8338 7.97333C15.8129 7.97556 17.0661 6.06757 17.2603 6.25658C17.4922 6.48212 16.218 9.70574 13.8394 9.70536C11.4604 9.70536 10.1869 6.481 10.4185 6.25658H10.4181Z"
            fill="black"
          />
          <path
            d="M10.7668 6.78414C10.9142 6.61414 12.0275 8.04831 13.8109 8.06695C15.6056 8.08559 16.7541 6.65701 16.9011 6.83186C17.0751 7.03876 15.7818 9.41313 13.8165 9.39897C11.8491 9.3848 10.5913 6.98694 10.7668 6.78451V6.78414Z"
            fill="black"
          />
          <path
            d="M17.2781 5.4998C17.1595 5.60567 15.8887 3.34575 13.9425 3.31071C11.9429 3.27492 10.5583 5.61239 10.436 5.4998C10.3031 5.37715 11.54 2.24711 13.9189 2.28588C16.256 2.32428 17.4083 5.38349 17.2781 5.4998Z"
            fill="black"
          />
          <path
            d="M8.25964 11.3399C8.27123 15.0773 7.36363 16.5263 8.31126 18.7799C9.25104 21.0152 11.2073 22.1191 11.8421 22.4774C15.0984 24.3153 18.3843 23.4049 19.067 23.1995C19.0614 24.3078 19.0558 25.4162 19.0506 26.5249C19.0506 27.5631 18.2088 28.4049 17.1703 28.4049H9.41789C8.37935 28.4049 7.5376 27.5631 7.5376 26.5249V5.66254C7.85447 6.90844 8.25253 8.88913 8.26001 11.3399H8.25964Z"
            fill="black"
          />
          <path
            d="M11.1497 28.2487C9.74153 28.5462 8.41904 28.1518 8.00191 27.3644C7.46468 26.35 8.23573 24.3179 10.4262 23.4783C10.4688 23.6987 10.8014 25.2749 12.3356 26.1316C13.8171 26.9588 15.2638 26.4701 15.4834 26.3918C15.0539 27.0106 14.6241 27.6298 14.1946 28.2487H11.1497Z"
            fill="black"
          />
          <path
            d="M17.7328 25.7126C17.7298 25.7066 17.7313 25.6995 17.7339 25.6939C17.924 22.9285 18.2345 18.6457 18.6845 13.3691C19.3116 6.01485 19.6187 3.66397 18.117 2.33941C15.5098 0.0399699 9.69273 2.81063 8.05374 3.64011C8.02194 3.38996 8.02157 3.09433 8.10013 2.77857C8.42187 1.48756 9.78775 0.954451 9.98042 0.882874H17.7328C18.7713 0.882874 19.6131 1.73174 19.6131 2.77857V23.8169C19.5993 24.0942 19.5248 24.7112 19.0624 25.1575C18.5719 25.6309 17.7713 25.7901 17.7328 25.7126Z"
            fill="black"
          />
          <path
            d="M17.2694 22.7928C17.134 22.9091 15.8343 20.5422 13.8721 20.5332C11.8953 20.5243 10.5679 22.9139 10.4276 22.7928C10.2735 22.6597 11.3865 19.3436 13.8485 19.344C16.3117 19.344 17.4198 22.6642 17.2694 22.7928Z"
            fill="black"
          />
          <path
            d="M10.4098 23.5735C10.6033 23.386 11.8505 25.2884 13.8255 25.2902C15.8046 25.2925 17.0578 23.3845 17.252 23.5735C17.4839 23.799 16.2097 27.0226 13.8311 27.0223C11.4521 27.0223 10.1786 23.7979 10.4102 23.5735H10.4098Z"
            fill="black"
          />
          <path
            d="M10.758 24.1009C10.9054 23.9309 12.0187 25.3651 13.8021 25.3837C15.5968 25.4024 16.7453 23.9738 16.8923 24.1486C17.0663 24.3555 15.773 26.7299 13.8078 26.7157C11.8403 26.7016 10.5825 24.3037 10.758 24.1013V24.1009Z"
            fill="black"
          />
          <path
            d="M17.2698 22.8166C17.1512 22.9224 15.8804 20.6625 13.9342 20.6275C11.9346 20.5917 10.55 22.9292 10.4277 22.8166C10.2948 22.6939 11.5317 19.5639 13.9107 19.6027C16.2477 19.6411 17.4 22.7003 17.2698 22.8166Z"
            fill="black"
          />
          <path
            d="M17.2694 14.1348C17.134 14.2511 15.8343 11.8842 13.8721 11.8753C11.8953 11.8663 10.5679 14.256 10.4276 14.1348C10.2735 14.0017 11.3865 10.6857 13.8485 10.686C16.3117 10.686 17.4198 14.0062 17.2694 14.1348Z"
            fill="black"
          />
          <path
            d="M10.4098 14.9155C10.6033 14.728 11.8505 16.6304 13.8255 16.6323C15.8046 16.6345 17.0578 14.7265 17.252 14.9155C17.4839 15.1411 16.2097 18.3647 13.8311 18.3643C11.4521 18.3643 10.1786 15.1399 10.4102 14.9155H10.4098Z"
            fill="black"
          />
          <path
            d="M10.758 15.4431C10.9054 15.2731 12.0187 16.7072 13.8021 16.7259C15.5968 16.7445 16.7453 15.3159 16.8923 15.4908C17.0663 15.6977 15.773 18.0721 13.8078 18.0579C11.8403 18.0437 10.5825 15.6459 10.758 15.4434V15.4431Z"
            fill="black"
          />
          <path
            d="M17.2698 14.1587C17.1512 14.2646 15.8804 12.0047 13.9342 11.9696C11.9346 11.9339 10.55 14.2713 10.4277 14.1587C10.2948 14.0361 11.5317 10.906 13.9107 10.9448C16.2477 10.9832 17.4 14.0424 17.2698 14.1587Z"
            fill="black"
          />
          <path
            d="M13.8592 8.63448C15.3556 8.63448 16.5686 7.41154 16.5686 5.90296C16.5686 4.39439 15.3556 3.17145 13.8592 3.17145C12.3629 3.17145 11.1499 4.39439 11.1499 5.90296C11.1499 7.41154 12.3629 8.63448 13.8592 8.63448Z"
            fill="white"
          />
          <path
            d="M13.8075 17.2191C15.3038 17.2191 16.5168 15.9961 16.5168 14.4876C16.5168 12.979 15.3038 11.756 13.8075 11.756C12.3112 11.756 11.0981 12.979 11.0981 14.4876C11.0981 15.9961 12.3112 17.2191 13.8075 17.2191Z"
            fill="white"
          />
          <path
            d="M13.8592 26.0638C15.3556 26.0638 16.5686 24.8409 16.5686 23.3323C16.5686 21.8237 15.3556 20.6008 13.8592 20.6008C12.3629 20.6008 11.1499 21.8237 11.1499 23.3323C11.1499 24.8409 12.3629 26.0638 13.8592 26.0638Z"
            fill="white"
          />
        </g>
        <defs>
          <filter
            id="filter0_d_8478_39751"
            x="0"
            y="0.264404"
            width="28"
            height="36.4712"
            filterUnits="userSpaceOnUse"
            colorInterpolationFilters="sRGB"
          >
            <feFlood floodOpacity="0" result="BackgroundImageFix" />
            <feColorMatrix
              in="SourceAlpha"
              type="matrix"
              values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0"
              result="hardAlpha"
            />
            <feOffset dy="4" />
            <feGaussianBlur stdDeviation="2" />
            <feComposite in2="hardAlpha" operator="out" />
            <feColorMatrix
              type="matrix"
              values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0.25 0"
            />
            <feBlend
              mode="normal"
              in2="BackgroundImageFix"
              result="effect1_dropShadow_8478_39751"
            />
            <feBlend
              mode="normal"
              in="SourceGraphic"
              in2="effect1_dropShadow_8478_39751"
              result="shape"
            />
          </filter>
        </defs>
      </svg>
    ),
    graphTitle: "Red Lights Missed",
    graphTitleDesc: "",
    graphNormValue: 0,
    isgraphNorm: false,
    graphActualValue: [score.redLightsMissed],
    graphActualValueLabel: [`${score.redLightsMissed}`],
    graphRange: [5, 10, 15, 20],
    graphRangeLabel: ["5", "10", "15", "20"],
    graphNoLines: [],
    minMax: false,
    graphUnit: "",
    graphAge: convertDOBintoAge(new Date(patientInfo?.dob)),
  };

  const metersLostOption = {
    graphName: "MetersLost",
    graphSVG: (
      <svg
        xmlns="http://www.w3.org/2000/svg"
        width="49"
        height="36"
        viewBox="0 0 49 36"
        fill="none"
      >
        <g filter="url(#filter0_d_4669_308)">
          <path
            d="M8.30103 14.8873C9.80847 17.4983 13.8029 20.9639 13.8029 20.9639L20.6651 19.0374C19.5292 17.0699 19.7243 15.6815 19.9195 14.293C20.1146 12.9045 20.3098 11.516 19.1739 9.54858C17.8392 7.23684 14.1512 2.57544 9.86232 3.77949C5.57347 4.98354 6.02923 10.9525 8.30103 14.8873ZM20.2631 25.2471C22.1555 24.7158 23.015 23.1075 22.1796 21.6606L21.4223 20.349L14.5602 22.2755L15.3174 23.5871C16.1528 25.034 18.3706 25.7784 20.2631 25.2471ZM38.7787 12.428C36.5069 8.49307 30.9046 3.969 26.6157 5.17305C22.3269 6.37709 24.2994 11.5201 25.6341 13.8318C26.77 15.7993 28.2903 16.7061 29.8107 17.613C31.3311 18.5199 32.8515 19.4267 33.9874 21.3942L40.8495 19.4677C40.8495 19.4677 40.2862 15.0389 38.7787 12.428ZM40.4475 25.6774C42.34 25.1462 43.1994 23.5378 42.3641 22.0909L41.6068 20.7793L34.7446 22.7058L35.5019 24.0174C36.3373 25.4643 38.5551 26.2087 40.4475 25.6774Z"
            fill="black"
          />
        </g>
        <defs>
          <filter
            id="filter0_d_4669_308"
            x="0.423875"
            y="0.494286"
            width="48.4394"
            height="34.6242"
            filterUnits="userSpaceOnUse"
            colorInterpolationFilters="sRGB"
          >
            <feFlood floodOpacity="0" result="BackgroundImageFix" />
            <feColorMatrix
              in="SourceAlpha"
              type="matrix"
              values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0"
              result="hardAlpha"
            />
            <feOffset dy="3.08982" />
            <feGaussianBlur stdDeviation="3.08982" />
            <feComposite in2="hardAlpha" operator="out" />
            <feColorMatrix
              type="matrix"
              values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0.25 0"
            />
            <feBlend
              mode="normal"
              in2="BackgroundImageFix"
              result="effect1_dropShadow_4669_308"
            />
            <feBlend
              mode="normal"
              in="SourceGraphic"
              in2="effect1_dropShadow_4669_308"
              result="shape"
            />
          </filter>
        </defs>
      </svg>
    ),
    graphTitle: "Meters Lost",
    graphTitleDesc: "(m)",
    graphNormValue: 0,
    isgraphNorm: false,
    graphActualValue: [score.metersLost],
    graphActualValueLabel: [`${score.metersLost}`],
    graphRange: [15, 30, 45, 60],
    graphRangeLabel: ["15", "30", "45", "60"],
    graphNoLines: [],
    minMax: false,
    graphUnit: "",
    graphAge: convertDOBintoAge(new Date(patientInfo?.dob)),
  };

  return (
    <>
      {score && !isNaN(score?.averageSpeed) && (
        <CustomBarGraph options={averageSpeedOption} />
      )}
      {score && !isNaN(score?.distance) && (
        <CustomBarGraph options={distanceOption} />
      )}
      {score && !isNaN(score?.redLightsMissed) && (
        <CustomBarGraph options={redLightsMissedOption} />
      )}
      {score && !isNaN(score?.metersLost) && (
        <CustomBarGraph options={metersLostOption} />
      )}
    </>
  );
};

export default RLGL_Test;
